import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { ClockModeThemeId } from '../../pages/v3/components/clock/themes/ClockModeThemeSettings';
import { TaskTimerAreaState } from '../../pages/v3/components/clock/components/TaskTimerArea';

/**
 * 时钟模式组件类型
 */
export type ClockModeComponentType =
  | 'empty'           // 空白
  | 'custom-text'     // 自定义文字
  | 'task-countdown'  // 任务倒数日
  | 'task-timer'      // 任务计时器
  | 'recent-tasks'    // 最近待办事项
  | 'today-efficiency'  // 今日效率
  | 'weekly-efficiency'; // 本周效率

/**
 * 待办事项时间范围类型
 */
export type TaskTimeRange = 'today' | 'today-tomorrow' | 'yesterday-today-tomorrow' | 'all-incomplete';

/**
 * 时钟模式区域配置
 */
export interface ClockModeAreaConfig {
  type: ClockModeComponentType;
  taskId?: string;      // 任务相关组件需要的任务ID（可选，不选择则为纯计时器）
  customText?: string;  // 自定义文字组件的内容
  countdownBase?: 'start' | 'due' | 'end'; // 倒数日基准时间
  timerDuration?: number; // 10 | 20 | 45 | 60; // 任务计时器时长（分钟）
  timerDurationSeconds?: number; // 任务计时器时长（秒），用于倒计时
  savedState?: TaskTimerAreaState;
  taskTimeRange?: TaskTimeRange; // 待办事项时间范围筛选
  soundNotification?: boolean; // 铃声提醒开关，默认false
  vibrationNotification?: boolean; // 震动提醒开关，默认false
  soundFile?: 'vivora' | 'delay' | 'sms'; // 音频文件选择，默认vivora
}

/**
 * 时钟模式配置
 */
export interface ClockModeConfig {
  area2: ClockModeAreaConfig;
  area3: ClockModeAreaConfig;
  area4: ClockModeAreaConfig;
  area5: ClockModeAreaConfig;
  area6: ClockModeAreaConfig;
}

/**
 * 时钟模式主题类型
 */
export type ClockModeTheme = ClockModeThemeId;

/**
 * 组件主题类型
 */
export type ComponentTheme = 'light' | 'dark';

export type ComponentMaterial = 'mica' | 'glass';

export interface ClockModeVisualConfig {
  componentMaterial: ComponentMaterial; // 组件材质
  useBackgroundBlur: boolean; // 是否使用背景模糊
}

/**
 * 时钟模式状态
 */
export interface ClockModeState {
  isFullscreen: boolean;
  keepScreenOn: boolean;
  orientation: 'auto' | 'portrait' | 'landscape';
  forceRotation: 'none' | 'rotate-90' | 'rotate-180' | 'rotate-270';
  theme: ClockModeTheme;
}

// 默认配置
const defaultClockModeConfig: ClockModeConfig = {
  area2: { type: 'empty' },
  area3: { type: 'empty' },
  area4: { type: 'empty' },
  area5: { type: 'empty' },
  area6: { type: 'empty' },
};

const defaultClockModeState: ClockModeState = {
  isFullscreen: false,
  keepScreenOn: false,
  orientation: 'auto',
  forceRotation: 'none',
  theme: 'light',
};

// 持久化的配置原子
export const clockModeConfigAtom = atomWithStorage<ClockModeConfig>(
  'clockModeConfig',
  defaultClockModeConfig
);

// 时钟模式状态原子 - 使用持久化存储
export const clockModeStateAtom = atomWithStorage<ClockModeState>(
  'clockModeState',
  defaultClockModeState
);

// 组件主题原子
export const componentThemeAtom = atomWithStorage<ComponentTheme>(
  'clockModeComponentTheme',
  'light'
);

export const clockModeThemeAtom = atomWithStorage<ClockModeTheme>(
  'clockModeTheme',
  'light'
);

export const clockModeVisualConfigAtom = atomWithStorage<ClockModeVisualConfig>(
  'clockModeVisualConfig',
  {
    componentMaterial: 'mica',
    useBackgroundBlur: false,
  }
);

// 自定义背景图片原子
export const customBackgroundImageAtom = atomWithStorage<string>(
  'clockModeCustomBackground',
  ''
);

// 是否处于配置模式
export const clockModeConfigModeAtom = atom<boolean>(false);

// 当前时间原子（用于统一时间更新）
export const clockModeTimeAtom = atom<Date>(new Date());

// 时间更新器原子
export const clockModeTimeUpdaterAtom = atom(
  null,
  (get, set) => {
    const updateTime = () => {
      set(clockModeTimeAtom, new Date());
    };
    
    // 每秒更新一次时间
    const interval = setInterval(updateTime, 1000);
    
    // 返回清理函数
    return () => clearInterval(interval);
  }
);

// 更新区域配置的原子
export const updateClockModeAreaAtom = atom(
  null,
  (get, set, { areaId, config }: { areaId: keyof ClockModeConfig; config: ClockModeAreaConfig }) => {
    const currentConfig = get(clockModeConfigAtom);
    set(clockModeConfigAtom, {
      ...currentConfig,
      [areaId]: config,
    });
  }
);

// 更新时钟模式状态的原子
export const updateClockModeStateAtom = atom(
  null,
  (get, set, updates: Partial<ClockModeState>) => {
    const currentState = get(clockModeStateAtom);
    set(clockModeStateAtom, {
      ...currentState,
      ...updates,
    });
  }
);
